import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { DropdownMenu } from '@codes/ui-kit/components/ui/dropdown-menu';
import { Image } from '@codes/ui-kit/components/ui/image';
import { useWorkspaceService } from '@web/app/features/home/hooks';
import { useCreateProjectDialog, useCreateWorkspaceDialog, useMyRole } from '@web/app/shared';
import { ToggleOption } from '@web/app/shared/components/ToggleOption';
import { useDialogStore, useSelectedStore } from '@web/app/shared/stores';

import { CodesLogoImage, LogoMiniDarkImage } from '@codes/assets';

import { ConfirmWorkspaceDialog } from '../../../confirm-workspace-dialog';
import { CreateProjectDialog } from '../../../create-project-dialog';
import { CreateWorkspaceDialogV2 } from '../../../create-workspace-dialog';
import { EnrollCardDialog } from '../../../enroll-card-dialog';
import { PricingPlansDialog } from '../../../pricing-plans-dialog';
import { getLightMenuItems, getMenuItems } from '../../consts';
import { UserBadge } from '../user-badge';
import { MobileSideBar } from './MobileSideBar';
import { ProjectSwitcher } from './ProjectSwitcher';
import { WorkspaceSwitcher } from './WorkspaceSwitcher';

export const Header = ({ headerRef }: { headerRef: React.RefObject<HTMLDivElement> }) => {
    const { workspace: myWorkspace, project: myProject } = useSelectedStore();
    const isWorkspaceSelected = !!myWorkspace;
    const { workspaceFromServer } = useWorkspaceService();
    const navigate = useNavigate();

    const { openWorkspaceConfirmDialog } = useDialogStore();

    const {
        setWorkspaceDialog,
        handleClose: handleCloseToCreateWorkspaceDialog,
        handleComplete: handleCompleteToCreateWorkspace,
        newWorkspace,
    } = useCreateWorkspaceDialog({
        onComplete: () => openWorkspaceConfirmDialog(),
    });

    const {
        setProjectDialog,
        handleClose: handleCloseToCreateProjectDialog,
        handleComplete: handleCompleteToCreateProject,
    } = useCreateProjectDialog({
        onComplete: newProject => {
            navigate(`/w/${workspaceFromServer?.id}/p/${newProject.id}`);
        },
    });

    const myRole = useMyRole();

    const menuItems = useMemo(() => {
        const items = !myProject
            ? getLightMenuItems(workspaceFromServer?.id)
            : getMenuItems(workspaceFromServer?.id, myProject?.id);

        if (myRole > 1) {
            return items.filter(item => !item.label.includes('settings'));
        }

        return items;
    }, [workspaceFromServer?.id, myProject, myRole]);

    const dialogs = (
        <>
            <PricingPlansDialog />
            <CreateWorkspaceDialogV2
                handleClose={handleCloseToCreateWorkspaceDialog}
                handleComplete={handleCompleteToCreateWorkspace}
            />
            <ConfirmWorkspaceDialog workspace={newWorkspace} />
            <EnrollCardDialog />
            {workspaceFromServer && (
                <CreateProjectDialog
                    workspace={workspaceFromServer}
                    handleClose={handleCloseToCreateProjectDialog}
                    handleComplete={handleCompleteToCreateProject}
                />
            )}
        </>
    );

    if (!isWorkspaceSelected) {
        return (
            <header
                ref={headerRef}
                className="fixed left-0 md:left-[60px] right-0 z-[99] flex flex-col bg-background/80 backdrop-blur-md border-b border-border/50"
            >
                <div className="flex flex-1 items-center justify-between h-12 px-6 max-md:px-4">
                    <div className="flex items-center gap-2">
                        <a href="/">
                            <Image src={CodesLogoImage} alt="Eureka Codes" className="h-[20px]" />
                        </a>
                    </div>
                    <div className="flex items-center gap-2">
                        <ToggleOption />
                        <UserBadge />
                    </div>
                </div>
                {dialogs}
            </header>
        );
    }

    return (
        <header
            ref={headerRef}
            className="fixed left-0 md:left-[60px] right-0 z-[99] flex flex-col bg-background/80 backdrop-blur-md border-b border-border/50"
        >
            {/* Single unified header bar */}
            <div className="flex items-center justify-between h-12 px-6 max-md:px-3">
                <div className="flex items-center gap-2">
                    {/* Mobile: hamburger + logo */}
                    {workspaceFromServer && (
                        <div className="md:hidden flex items-center gap-1.5">
                            <MobileSideBar items={menuItems} />
                        </div>
                    )}
                    <button
                        className="md:hidden"
                        onClick={() => navigate(workspaceFromServer?.id ? `/w/${workspaceFromServer.id}` : '/w')}
                    >
                        <Image className="w-[20px] h-[20px]" src={LogoMiniDarkImage} />
                    </button>

                    {/* Desktop: workspace/project switcher */}
                    <div className="flex items-center max-md:hidden">
                        <DropdownMenu>
                            <WorkspaceSwitcher onOpenCreateWorkspace={() => setWorkspaceDialog(true)} />
                        </DropdownMenu>
                        <DropdownMenu>
                            <ProjectSwitcher onOpenCreateProject={() => setProjectDialog(true)} />
                        </DropdownMenu>
                    </div>

                    {/* Mobile: compact workspace/project pills */}
                    <div className="md:hidden flex items-center gap-1 overflow-hidden">
                        <DropdownMenu>
                            <WorkspaceSwitcher onOpenCreateWorkspace={() => setWorkspaceDialog(true)} />
                        </DropdownMenu>
                        <DropdownMenu>
                            <ProjectSwitcher onOpenCreateProject={() => setProjectDialog(true)} />
                        </DropdownMenu>
                    </div>
                </div>

                <div className="flex items-center gap-1.5">
                    <ToggleOption />
                    <UserBadge />
                </div>
            </div>

            {dialogs}
        </header>
    );
};
